'use client'

import { type AnalyticsBrowser } from '@segment/analytics-next'
import Cookies from 'js-cookie'
import { useEffect } from 'react'

import { COOKIE_NAMES } from 'lib/constants'
import { segmentAnalytics } from 'lib/segmentAnalytics'
import { getExperiment } from 'utils/'
import { getIsInternalUser } from 'utils/getIsInternalUser'
import getUserAgentInfo from 'utils/getUserAgentInfo'
import useNetworkState from 'utils/use-network-status'

const doExternalUserClientSideSetupOfSegmentAnalytics = ({
  anonymousId,
  segmentAnalyticsInstance,
  networkState,
}: {
  anonymousId?: string
  segmentAnalyticsInstance: AnalyticsBrowser
  networkState: ReturnType<typeof useNetworkState>
}) => {
  const experiment = getExperiment()

  const data = {
    band: experiment
      ? `${experiment.experiment_name}.${experiment.variation_id}`
      : 'baseline',
    experiment: experiment || null,
    version: '1.1',
  }

  segmentAnalyticsInstance.track('Traffic Allocated', data)

  const userAgentInfo = getUserAgentInfo()

  const ga_client_id = Cookies.get(COOKIE_NAMES.googleAnalyticsClientId)

  segmentAnalyticsInstance.page(
    window.location.pathname + window.location.search,
    {
      pixel_ratio: window.devicePixelRatio,
      screen_height: window.screen.height,
      screen_width: window.screen.width,
      tz_offset: new Date().getTimezoneOffset(),
      userAgent: userAgentInfo,
      network: networkState,
      ga_client_id: ga_client_id,
    },
  )

  segmentAnalyticsInstance.ready(() => {
    if (anonymousId) {
      segmentAnalyticsInstance.identify(anonymousId)
    }
  })
}

const doClientSideSetupOfSegmentAnalytics = ({
  segmentAnalyticsInstance,
  networkState,
}: {
  segmentAnalyticsInstance: AnalyticsBrowser
  networkState: ReturnType<typeof useNetworkState>
}) => {
  const anonymousId = Cookies.get(COOKIE_NAMES.anonymousId)

  if (anonymousId) {
    segmentAnalyticsInstance.setAnonymousId(anonymousId)
  }

  const isInternalUser = getIsInternalUser()

  if (!isInternalUser) {
    doExternalUserClientSideSetupOfSegmentAnalytics({
      anonymousId,
      segmentAnalyticsInstance,
      networkState,
    })
  }
}

export const SegmentAnalyticsClientSideSetupTrigger: React.FC = () => {
  const networkState = useNetworkState()

  useEffect(() => {
    doClientSideSetupOfSegmentAnalytics({
      segmentAnalyticsInstance: segmentAnalytics,
      networkState,
    })
  }, [])

  return null
}
